/**
 * FillableFormFiller - Caregiver side
 *
 * Renders a fillable PDF from the caregiver's assigned forms and
 * overlays inputs (text/date/checkbox) or signature buttons on each
 * field marked assignee=employee. On submit, the values are POSTed to
 * the backend which flattens them onto the original PDF.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import { Document, Page, pdfjs } from "react-pdf";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Loader2, X, Check, Pencil, RotateCcw, Clock, Undo2, Eraser } from "lucide-react";
import InkCanvas, { exportInkPng } from "./InkCanvas";
import FreeTextAnnot from "./FreeTextAnnot";
import FillerToolbar from "./FillerToolbar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`; // served locally — no CDN dependency

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCALE = 1.2;

// react-signature-canvas 1.1.0-alpha's getTrimmedCanvas() is broken (trim_canvas import); trim ourselves
function trimCanvas(canvas) {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  const bg = [data[0], data[1], data[2]];
  let top = height, left = width, right = -1, bottom = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const isInk = data[i + 3] > 0 && (Math.abs(data[i] - bg[0]) + Math.abs(data[i + 1] - bg[1]) + Math.abs(data[i + 2] - bg[2]) > 60);
      if (isInk) { if (x < left) left = x; if (x > right) right = x; if (y < top) top = y; if (y > bottom) bottom = y; }
    }
  }
  if (right < 0) return canvas;
  const pad = 6;
  left = Math.max(0, left - pad); top = Math.max(0, top - pad);
  right = Math.min(width - 1, right + pad); bottom = Math.min(height - 1, bottom + pad);
  const out = document.createElement("canvas");
  out.width = right - left + 1; out.height = bottom - top + 1;
  out.getContext("2d").putImageData(ctx.getImageData(left, top, out.width, out.height), 0, 0);
  return out;
}

export default function FillableFormFiller({
  open,
  onClose,
  form,
  onSubmitted,
  pdfEndpoint, // optional: where to fetch the blank PDF (defaults to caregiver vault endpoint)
  onSubmitValues, // optional: async (values) => void — replaces the default POST
  submitLabel,
  title, // optional header title
  onSaveDraft, // optional: async (values) => void — shows a "Save draft" button + autosave
  previewMode, // optional: read-only walkthrough — no Submit, banner "nothing is saved"
  confirmSubmit, // optional: message shown in a confirm dialog before submitting (final, non-editable forms)
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tool, setTool] = useState("fill"); // fill | draw | text
  const [penColor, setPenColor] = useState("#111827");
  const [penWidth, setPenWidth] = useState(2);
  const [inkMode, setInkMode] = useState("pen"); // pen | eraser
  const [eraserWidth, setEraserWidth] = useState(16);
  const [ink, setInk] = useState({}); // {`p${page}` | fieldId: strokes[]} — this session's strokes (undo-able)
  const [inkBase, setInkBase] = useState({}); // saved PNG that was on file when drawing started (null after Clear)
  const inkBaseRef = useRef({});
  const [activeAnnot, setActiveAnnot] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [pageSizes, setPageSizes] = useState([]);
  const [pageRenderInfo, setPageRenderInfo] = useState({}); // { pageIdx: {width, height} in px }
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [sigModal, setSigModal] = useState(null); // fieldId of open signature modal
  const sigCanvasW = Math.min(440, (typeof window !== "undefined" ? window.innerWidth : 480) - 80); // 1:1 pixels — no CSS scaling so strokes land under the finger
  const [containerWidth, setContainerWidth] = useState(0);
  const sigRef = useRef(null);
  const scrollRef = useRef(null);
  const valuesRef = useRef({});
  valuesRef.current = values;

  // Fit the page to narrow screens (tablet / phone) — never upscale past SCALE
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, loading]);
  const firstPageWidth = (pageSizes[0] || [612])[0];
  const renderScale = containerWidth > 0 ? Math.min(SCALE, Math.max(0.4, (containerWidth - 24) / firstPageWidth)) : SCALE;

  const saveDraft = async (silent = false, explicitValues = null) => {
    if (!onSaveDraft) return;
    setSavingDraft(true);
    try {
      await onSaveDraft(explicitValues || valuesRef.current);
      setDirty(false);
      if (!silent) toast.success("Draft saved");
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || "Could not save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  // Autosave every 30s while there are unsaved changes
  useEffect(() => {
    if (!open || !onSaveDraft) return;
    const t = setInterval(() => { if (dirty) saveDraft(true); }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onSaveDraft, dirty]);

  const fields = form?.fillable_form?.fields || [];
  const autoIds = useMemo(
    () => new Set(form?.auto_filled || Object.keys(form?.default_values || {})),
    [form]
  );
  const prevValues = form?.previous_values || null; // recurring updates: prior version's values → live "changed" highlight
  const changedIds = useMemo(() => {
    if (!prevValues) return new Set(form?.changed_fields || []);
    return new Set(fields.filter((f) => f.visit_slot == null && !f.locked && (values[f.id] || "") !== (prevValues[f.id] || "")).map((f) => f.id));
  }, [prevValues, values, fields, form]);
  const employeeFields = useMemo(
    () => fields.filter((f) => f.assignee === "employee" && !f.locked),
    [fields]
  );

  useEffect(() => {
    if (!open || !form) return;
    let currentUrl = null;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setValues(form.default_values || {});
      setInk({});
      inkBaseRef.current = {};
      setInkBase({});
      setTool("fill");
      try {
        const res = await axios.get(
          pdfEndpoint || `${API}/caregiver/me/fillable-forms/${form.id}/pdf`,
          { responseType: "blob" }
        );
        if (cancelled) return;
        currentUrl = URL.createObjectURL(res.data);
        setPdfUrl(currentUrl);
        setPageCount(form.fillable_form?.page_count || 1);
        setPageSizes(form.fillable_form?.page_sizes || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not load form");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form]);

  const setValue = (id, v) => { setDirty(true); setValues((prev) => ({ ...prev, [id]: v })); };

  // --- freestyle writing: page ink layers, drawing fields, typed notes ---
  const inkKey = (pageIdx) => `__ink_p${pageIdx}`;
  const inkBaseFor = (key, valueKey) => (key in inkBaseRef.current ? inkBaseRef.current[key] : valuesRef.current[valueKey] || null);
  const commitInk = async (key, valueKey, strokes, wPts, hPts) => {
    const base = inkBaseFor(key, valueKey);
    inkBaseRef.current = { ...inkBaseRef.current, [key]: base };
    setInkBase(inkBaseRef.current);
    setInk((prev) => ({ ...prev, [key]: strokes }));
    const png = strokes.length || base ? await exportInkPng(strokes, base, wPts, hPts) : "";
    setValue(valueKey, png);
  };
  const undoInk = (key, valueKey, wPts, hPts) => {
    const cur = ink[key] || [];
    if (!cur.length) { toast.info("Nothing to undo from this session — use Clear to erase the saved drawing"); return; }
    commitInk(key, valueKey, cur.slice(0, -1), wPts, hPts);
  };
  const clearInk = (key, valueKey) => {
    inkBaseRef.current = { ...inkBaseRef.current, [key]: null };
    setInkBase(inkBaseRef.current);
    setInk((prev) => ({ ...prev, [key]: [] }));
    setValue(valueKey, "");
  };
  const displayBase = (key, valueKey) => (key in inkBase ? inkBase[key] : values[valueKey] || null);
  const annots = Array.isArray(values.__annots) ? values.__annots : [];
  const setAnnots = (next) => setValue("__annots", next);
  const addAnnot = (pageIdx, e, info, wPts, hPts) => {
    const r = e.currentTarget.getBoundingClientRect();
    const scale = info.width / wPts;
    const xPt = (e.clientX - r.left) / scale;
    const yTopPt = (e.clientY - r.top) / scale;
    const a = { id: `a_${Date.now().toString(36)}`, page: pageIdx, x: Math.max(0, xPt - 2), y: Math.max(0, hPts - yTopPt - 8), width: Math.min(220, wPts - xPt), height: 16, text: "", font_size: 10 };
    setAnnots([...annots, a]);
    setActiveAnnot(a.id);
  };

  const openSignature = (fieldId) => {
    setSigModal(fieldId);
    setTimeout(() => sigRef.current?.clear(), 50);
  };

  const saveSignature = () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error("Please sign first");
      return;
    }
    const dataUrl = trimCanvas(sigRef.current.getCanvas()).toDataURL("image/png");
    const next = { ...valuesRef.current, [sigModal]: dataUrl };
    setValue(sigModal, dataUrl);
    setSigModal(null);
    saveDraft(true, next); // persist immediately — a signature must never be lost on close
  };

  const closeFiller = async () => {
    if (onSaveDraft && dirty) await saveDraft(true);
    onClose();
  };

  const workOnLater = async () => {
    await saveDraft(true);
    toast.success("Saved — pick it up any time from In progress / Continue");
    onClose();
  };

  const submit = async () => {
    // Validate required
    for (const f of employeeFields) {
      if (f.required && !f.locked) {
        const v = values[f.id];
        if (v === undefined || v === "" || v === null || v === false) {
          toast.error(`"${f.label}" is required`);
          return;
        }
      }
    }
    if (confirmSubmit && !confirmOpen) { setConfirmOpen(true); return; }
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      if (onSubmitValues) {
        const res = await onSubmitValues(values);
        if (res === false) return; // caller keeps control (e.g. follow-up step)
      } else {
        await axios.post(
          `${API}/caregiver/me/fillable-forms/${form.id}/submit`,
          { values }
        );
        toast.success("Form submitted — HR will review it shortly");
      }
      onSubmitted?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Group fields per page
  const fieldsByPage = useMemo(() => {
    const map = {};
    fields.forEach((f) => (map[f.page] ||= []).push(f));
    return map;
  }, [fields]);

  const filledCount = employeeFields.filter(
    (f) => values[f.id] !== undefined && values[f.id] !== "" && values[f.id] !== false
  ).length;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && closeFiller()}>
        <DialogContent
          className="max-w-5xl w-[96vw] h-[92vh] p-0 flex flex-col"
          data-testid="fillable-form-filler"
          aria-describedby={undefined}
        >
          <DialogHeader className="px-4 py-3 border-b flex flex-row items-center justify-between gap-2 space-y-0">
            <DialogTitle className="text-base truncate flex-1 pr-4">
              {title || `Fill: ${form?.filename || form?.title || "form"}`}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 hidden sm:inline">
                {filledCount} / {employeeFields.length} filled
              </span>
              {form?.has_previous_version && (
                <span className="text-[11px] rounded-full bg-orange-100 text-orange-800 px-2 py-0.5 hidden sm:inline" title="Fields that differ from the previous version are outlined in orange" data-testid="changed-count">
                  {changedIds.size} changed vs previous
                </span>
              )}
              {autoIds.size > 0 && (
                <span className="text-[11px] rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 hidden sm:inline" data-testid="auto-filled-count">
                  {autoIds.size} auto-filled
                </span>
              )}
              {onSaveDraft && !previewMode && (
                <>
                  <span className="text-[11px] text-slate-400 hidden md:inline" data-testid="draft-status">
                    {savingDraft ? "Saving…" : dirty ? "Unsaved changes" : "All changes saved"}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={workOnLater}
                    disabled={savingDraft || loading || submitting}
                    className="gap-1 border-amber-300 text-amber-800 hover:bg-amber-50"
                    title="Save everything entered so far and come back to it later — nothing is submitted"
                    data-testid="save-draft-btn"
                  >
                    {savingDraft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clock className="w-3.5 h-3.5" />}
                    <span className="hidden sm:inline">Work on later</span>
                  </Button>
                </>
              )}
              {previewMode && (
                <span className="text-[11px] rounded-full bg-sky-100 text-sky-800 px-2 py-0.5" data-testid="preview-banner">
                  Preview — nothing is saved
                </span>
              )}
              {!previewMode && <Button
                size="sm"
                onClick={submit}
                disabled={submitting || loading}
                className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                data-testid="submit-fillable-btn"
              >
                {submitting ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Check className="w-3.5 h-3.5" />
                )}
                {submitLabel || "Submit"}
              </Button>}
              <Button size="sm" variant="ghost" onClick={closeFiller} aria-label="Close">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </DialogHeader>
          {!previewMode && (
            <FillerToolbar
              tool={tool} setTool={setTool} penColor={penColor} setPenColor={setPenColor} penWidth={penWidth} setPenWidth={setPenWidth}
              inkMode={inkMode} setInkMode={setInkMode} eraserWidth={eraserWidth} setEraserWidth={setEraserWidth}
            />
          )}

          <div ref={scrollRef} className="flex-1 overflow-y-auto bg-slate-200 p-2 sm:p-4">
            {loading && (
              <div className="h-full flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
              </div>
            )}
            {pdfUrl && !loading && (
              <Document
                file={pdfUrl}
                onLoadError={(e) => toast.error("PDF load error: " + e.message)}
                loading={<Loader2 className="w-5 h-5 animate-spin text-slate-500" />}
              >
                {Array.from({ length: pageCount }, (_, i) => (
                  <div key={i} className="mb-4 flex justify-center">
                    <div
                      className="relative shadow-md bg-white inline-block"
                      onClick={(e) => {
                        const info = pageRenderInfo[i];
                        if (tool === "text" && info) addAnnot(i, e, info, (pageSizes[i] || [612, 792])[0], (pageSizes[i] || [612, 792])[1]);
                      }}
                      style={tool === "text" ? { cursor: "text" } : undefined}
                      data-testid={`filler-page-${i}`}
                    >
                      {pageRenderInfo[i] && (() => {
                        const info = pageRenderInfo[i];
                        const [wPts, hPts] = pageSizes[i] || [612, 792];
                        const key = `p${i}`;
                        const vk = inkKey(i);
                        return (
                          <>
                            <InkCanvas
                              width={info.width}
                              height={info.height}
                              scale={info.width / wPts}
                              strokes={ink[key] || []}
                              baseImage={displayBase(key, vk)}
                              color={penColor}
                              penWidth={penWidth}
                              eraser={inkMode === "eraser"}
                              eraserWidth={eraserWidth}
                              active={tool === "draw"}
                              onStrokeEnd={(strokes) => commitInk(key, vk, strokes, wPts, hPts)}
                              className="absolute inset-0"
                              style={{ zIndex: tool === "draw" ? 30 : 2 }}
                              testId={`ink-layer-${i}`}
                            />
                            {tool === "draw" && (
                              <div className="absolute top-1 right-1 z-40 flex gap-1" data-testid={`ink-tools-${i}`}>
                                <Button size="sm" variant="secondary" className="h-6 px-2 text-[11px] gap-1 shadow" onClick={(e) => { e.stopPropagation(); undoInk(key, vk, wPts, hPts); }} data-testid={`ink-undo-${i}`}>
                                  <Undo2 className="w-3 h-3" /> Undo
                                </Button>
                                <Button size="sm" variant="secondary" className="h-6 px-2 text-[11px] gap-1 shadow text-rose-700" onClick={(e) => { e.stopPropagation(); clearInk(key, vk); }} data-testid={`ink-clear-${i}`}>
                                  <Eraser className="w-3 h-3" /> Clear page
                                </Button>
                              </div>
                            )}
                            {annots.filter((a) => a.page === i).map((a) => (
                              <FreeTextAnnot
                                key={a.id}
                                annot={a}
                                scale={info.width / wPts}
                                pageHeightPts={hPts}
                                autoFocus={activeAnnot === a.id}
                                onChange={(next) => setAnnots(annots.map((x) => (x.id === a.id ? next : x)))}
                                onRemove={() => setAnnots(annots.filter((x) => x.id !== a.id))}
                              />
                            ))}
                          </>
                        );
                      })()}
                      <Page
                        pageNumber={i + 1}
                        scale={renderScale}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        onRenderSuccess={({ width, height }) => {
                          setPageRenderInfo((prev) => {
                            const existing = prev[i];
                            if (existing && existing.width === width && existing.height === height) return prev;
                            return { ...prev, [i]: { width, height } };
                          });
                        }}
                      />

                      {(fieldsByPage[i] || []).map((f) => {
                        const info = pageRenderInfo[i];
                        if (!info) return null;
                        const [wPts] = pageSizes[i] || [612, 792];
                        const scale = info.width / wPts;
                        const left = f.x * scale;
                        const bottom = f.y * scale;
                        const w = f.width * scale;
                        const h = f.height * scale;
                        const top = info.height - bottom - h;

                        const isHR = f.assignee === "hr";
                        const val = values[f.id];

                        if (f.locked) {
                          const future = f.slot_state === "disabled";
                          const fontPx = Math.max(9, Math.min(14, h * 0.6));
                          return (
                            <div
                              key={f.id}
                              className={`absolute border border-dashed flex items-center overflow-hidden px-0.5 ${future ? "border-slate-300 bg-slate-100/70" : "border-sky-300 bg-sky-50/60"}`}
                              style={{ left, top, width: w, height: h, fontSize: fontPx }}
                              title={future ? `${f.label} — completed at a later supervisory visit` : `${f.label} — signed at an earlier visit (locked)`}
                              data-testid={`locked-field-${f.id}`}
                            >
                              {f.type === "signature" || f.type === "ink" ? (val ? <img src={val} alt="signed" className="max-w-full max-h-full object-contain" /> : null)
                                : f.type === "checkbox" ? (val ? <span className="font-bold text-slate-700 leading-none">X</span> : null)
                                : <span className="text-slate-700 whitespace-nowrap leading-none">{val || ""}</span>}
                              {future && !val && <span className="text-[9px] text-slate-400 italic truncate">next visit</span>}
                            </div>
                          );
                        }

                        if (f.assignee === "rn") {
                          return (
                            <div
                              key={f.id}
                              className="absolute border border-dashed border-rose-300 bg-rose-50/50 pointer-events-none flex items-center justify-center overflow-hidden px-0.5"
                              style={{ left, top, width: w, height: h, fontSize: Math.max(8, Math.min(11, h * 0.5)) }}
                              title={`${f.label} — completed by the RN after you submit`}
                              data-testid={`rn-field-${f.id}`}
                            >
                              <span className="text-rose-700 whitespace-nowrap leading-none italic">RN signs here</span>
                            </div>
                          );
                        }

                        if (isHR) {
                          const hrVal = form?.hr_values?.[f.id];
                          const fontPx = Math.max(9, Math.min(14, h * 0.6));
                          return (
                            <div
                              key={f.id}
                              className="absolute border border-dashed border-purple-300 bg-purple-50/40 pointer-events-none flex items-center overflow-hidden px-0.5"
                              style={{ left, top, width: w, height: h, fontSize: fontPx }}
                              title={`${f.label} — pre-filled by HR`}
                              data-testid={`hr-field-${f.id}`}
                            >
                              {f.type === "checkbox" ? (
                                hrVal ? <span className="font-bold text-slate-800 leading-none">X</span> : null
                              ) : (
                                <span className="text-slate-800 whitespace-nowrap leading-none">{hrVal || ""}</span>
                              )}
                            </div>
                          );
                        }

                        if (f.type === "text" || f.type === "date") {
                          const isAuto = autoIds.has(f.id);
                          const isChanged = changedIds.has(f.id);
                          return (
                            <div key={f.id} className={`absolute ${isChanged ? "ring-2 ring-orange-400 ring-offset-1" : ""}`} style={{ left, top, width: w, height: h }} title={isChanged ? `${f.label} — changed from the previous version${prevValues ? ` (was: ${prevValues[f.id] || "blank"})` : ""}` : undefined} data-testid={isChanged ? `changed-field-${f.id}` : undefined}>
                              <input
                                type={f.type === "date" ? "date" : "text"}
                                value={val || ""}
                                onChange={(e) => setValue(f.id, e.target.value)}
                                placeholder={f.label}
                                className={`w-full h-full border-2 ${isAuto ? "border-emerald-400 bg-emerald-50/70" : "border-blue-400 bg-yellow-50"} text-slate-900 px-1 text-sm focus:border-blue-600 outline-none ${f.required && !val ? "ring-2 ring-red-300" : ""}`}
                                style={{ fontSize: Math.max(10, h * 0.55) }}
                                title={isAuto ? `${f.label} — filled in automatically; you can edit it` : f.label}
                                data-testid={`fill-field-${f.id}`}
                              />
                              {isAuto && (
                                <span
                                  className="absolute -top-2 -right-1 rounded-full bg-emerald-600 text-white text-[8px] leading-none px-1 py-0.5 pointer-events-none"
                                  data-testid={`auto-marker-${f.id}`}
                                >
                                  auto
                                </span>
                              )}
                            </div>
                          );
                        }

                        if (f.type === "checkbox") {
                          const isAuto = autoIds.has(f.id);
                          return (
                            <label
                              key={f.id}
                              className={`absolute border-2 ${isAuto ? "border-emerald-500 bg-emerald-50" : "border-amber-400 bg-yellow-50"} flex items-center justify-center cursor-pointer`}
                              style={{ left, top, width: w, height: h }}
                              title={isAuto ? `${f.label} — ticked automatically from the record; you can change it` : f.label}
                              data-testid={isAuto ? `auto-marker-${f.id}` : undefined}
                            >
                              <input
                                type="checkbox"
                                checked={!!val}
                                onChange={(e) => setValue(f.id, e.target.checked)}
                                className="w-4 h-4"
                                data-testid={`fill-field-${f.id}`}
                              />
                            </label>
                          );
                        }

                        if (f.type === "ink") {
                          const strokes = ink[f.id] || [];
                          return (
                            <div key={f.id} className={`absolute border-2 border-dashed border-indigo-400 bg-indigo-50/40 ${f.required && !val ? "ring-2 ring-red-300" : ""}`} style={{ left, top, width: w, height: h, zIndex: 3 }} title={`${f.label} — draw or write inside this box`} data-testid={`fill-field-${f.id}`}>
                              <InkCanvas
                                width={w}
                                height={h}
                                scale={scale}
                                strokes={strokes}
                                baseImage={displayBase(f.id, f.id)}
                                color={penColor}
                                penWidth={penWidth}
                                eraser={tool === "draw" && inkMode === "eraser"}
                                eraserWidth={eraserWidth}
                                active={tool !== "text"}
                                onStrokeEnd={(next) => commitInk(f.id, f.id, next, f.width, f.height)}
                                testId={`ink-field-${f.id}`}
                              />
                              {!val && <span className="absolute left-1 top-0.5 text-[10px] text-indigo-500 pointer-events-none flex items-center gap-1"><Pencil className="w-3 h-3" /> {f.label}</span>}
                              {val && (
                                <button type="button" onClick={() => clearInk(f.id, f.id)} className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-rose-500 text-white flex items-center justify-center" title="Clear drawing" data-testid={`ink-field-clear-${f.id}`}>
                                  <X className="w-2.5 h-2.5" />
                                </button>
                              )}
                            </div>
                          );
                        }

                        if (f.type === "signature") {
                          return (
                            <button
                              key={f.id}
                              onClick={() => openSignature(f.id)}
                              className={`absolute border-2 border-purple-500 bg-yellow-50 hover:bg-yellow-100 flex items-center justify-center gap-1 text-xs text-purple-800 ${f.required && !val ? "ring-2 ring-red-300" : ""}`}
                              style={{ left, top, width: w, height: h }}
                              data-testid={`fill-field-${f.id}`}
                            >
                              {val ? (
                                <img src={val} alt="sig" className="max-w-full max-h-full object-contain" />
                              ) : (
                                <>
                                  <Pencil className="w-3 h-3" /> Sign
                                </>
                              )}
                            </button>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                ))}
              </Document>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="submit-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{submitLabel || "Submit"}?</AlertDialogTitle>
            <AlertDialogDescription>{confirmSubmit}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="submit-confirm-cancel">Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-confirm-ok">
              Yes, {(submitLabel || "submit").toLowerCase()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Signature capture sub-modal */}
      <Dialog open={!!sigModal} onOpenChange={(v) => !v && setSigModal(null)}>
        <DialogContent className="max-w-md" data-testid="signature-modal">
          <DialogHeader>
            <DialogTitle>Draw your signature</DialogTitle>
          </DialogHeader>
          <div className="border rounded-lg bg-slate-50 overflow-hidden">
            <SignatureCanvas
              ref={sigRef}
              clearOnResize={false}
              canvasProps={{
                width: sigCanvasW,
                height: 180,
                className: "sig-canvas block touch-none",
                style: { width: sigCanvasW, height: 180 },
              }}
              backgroundColor="#f8fafc"
            />
          </div>
          <div className="flex justify-between gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => sigRef.current?.clear()}
              className="gap-1"
              data-testid="signature-clear-btn"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Clear
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSigModal(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={saveSignature}
                className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                data-testid="signature-save-btn"
              >
                <Check className="w-3.5 h-3.5" /> Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
